import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { ProductCategoryFullDTOAndTags } from '../../../models/taggable.model';
import { API, ProductCategoryFullDTO } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../services/tag.service';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { switchMap } from 'rxjs';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit';
type ProductCategoryFullDTOAndTagsAndButtons = ProductCategoryFullDTOAndTags & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-product-categories',
  templateUrl: './product-categories.component.html',
  styleUrls: ['./product-categories.component.scss'],
  standalone: false,
})
export class ProductCategoriesComponent extends RequestClass implements OnInit {
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  public readonly tagService = inject(TagService);
  public readonly permissionService = inject(PermissionService);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly api = inject(API);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  isInModalContext: boolean;
  modalType: string;
  queryParamsParsed: boolean = false;

  // table wrapper variables
  productCategories: ProductCategoryFullDTOAndTagsAndButtons[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit() {
    if (this.isInModalContext) {
      this.modalType = this.modalNavigation.getCurrentNavigation()?.data?.modalType;
    }
    this.queryParamsParsed = true;
  }

  selectCategory(cat: ProductCategoryFullDTO) {
    if (this.isInModalContext) {
      this.modalNavigation.result(cat);
    }
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .pm_ProductCategory_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          order: null,
        },
        this.pagination
      )
      .pipe(
        switchMap(cats => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.ProductCategory, cats)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (productCategories: ProductCategoryFullDTOAndTags[]) => {
          this.productCategories = this.buildSplitButtons(productCategories);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.product_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.productCategory.edit',
          link: RoutingID.productCategoryEdit,
          pathParams: [{ id: 'productCategoryID', value: item?.id }],
        });
      }
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
